import Autosuggest, {
    ChangeEvent,
    RenderSuggestionParams,
    RenderSuggestionsContainerParams,
    SuggestionSelectedEventData,
    SuggestionsFetchRequestedParams,
} from 'react-autosuggest';
import {
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Popper,
    TextField,
    Theme,
} from '@mui/material';
import { Component, FocusEvent, FormEvent, MouseEvent, ReactElement, ReactNode, createRef } from 'react';
import { PoolTextField } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { tokens } from '@synergy/theme-palo';
import ClearIcon from '@mui/icons-material/Clear';
import parse from 'autosuggest-highlight/parse';

import { match } from './utils/match';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            flexGrow: 1,
            position: 'relative',
            display: 'flex',
        },
        suggestionsContainerOpen: {
            display: 'block',
            position: 'relative',
            top: 4,
            left: 0,
            right: 0,
            marginBottom: 10,

            maxHeight: 350,
            minHeight: 0,
            overflowY: 'auto',
            zIndex: 2, // set to 2 because the headlines that may be underneath the container have zIndex 1

            width: '100%',
            border: `1px solid ${tokens.color.line}`,
            borderRadius: tokens.radius.default,
            backgroundColor: tokens.color.surface,
            boxShadow: tokens.shadow.s2,
            '&::-webkit-scrollbar': {
                width: theme.scrollbars.width,
            },
            '&::-webkit-scrollbar-thumb': {
                background: theme.scrollbars.color,
                borderRadius: theme.scrollbars.borderRadius,
            },
            '&::-webkit-scrollbar-track': {
                background: theme.scrollbars.backgroundColor,
                borderRadius: theme.scrollbars.borderRadius,
            },
        },
        suggestionFooter: {
            position: 'fixed',
            bottom: theme.spacing(),
            left: 0,
            right: +theme.scrollbars.width + 8,
            backgroundColor: tokens.color.surface,
        },
        suggestionFooterListWrapper: {
            '& > ul': {
                marginBottom: 44,
            },
        },
        suggestion: {
            display: 'block',
        },
        suggestionsList: {
            margin: 0,
            padding: 0,
            listStyleType: 'none',
            width: '100%',
            display: 'table',

            ' & > li': {
                display: 'table-row-group',

                '&:last-child > div > div': {
                    borderBottom: 'none',
                },
            },
        },
        highlighted: {
            color: tokens.color.accent,
            fontWeight: 600,
        },
        // A table cell ignores max-width, so the block inside it carries it.
        truncated: {
            display: 'block',
            maxWidth: 360,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        suggestionItem: {
            display: 'table-row',
            fontSize: '13px',
            color: tokens.color.ink,

            '&:hover': {
                backgroundColor: tokens.color.accentSoft,
            },

            '& > div': {
                display: 'table-cell',
                padding: '8px 12px',
                verticalAlign: 'middle',
            },

            '& > span:last-of-type': {
                display: 'none',
            },
        },
        suggestionActionContainer: {
            '& > div:last-of-type': {
                padding: theme.spacing(0, 2, 0, 0),
            },

            '& > div:last-of-type > div': {
                textAlign: 'right',
                visibility: 'hidden',
            },

            '&:hover > div:last-of-type > div': {
                visibility: 'visible',
            },
        },
        suggestionActions: {
            '& svg': {
                color: tokens.color.ink3,
                fontSize: '16px',
            },
        },
        inputDisabled: {
            cursor: 'not-allowed',
            opacity: 0.38,
        },
        readOnly: {
            backgroundColor: theme.palette.background.default,
            border: 'none',
            borderRadius: '4px',
            '& > div': {
                border: '1px solid rgba(33, 39, 46, .27)',
                opacity: 0.6,
                color: '#21272E',
            },
            '& > label': {
                color: '#21272E',
                opacity: 0.6,
            },
        },
        // The way to empty the field shows where the field is being worked on.
        // Standing there all the time it took 40 pixels of a narrow field, and
        // a name in a document head was cut down to its first letters (#2262).
        clearGroup: {
            display: 'none',
            alignItems: 'center',
        },
        field: {
            '&:hover $clearGroup, &:focus-within $clearGroup': {
                display: 'inline-flex',
            },
        },
        clearButton: {
            opacity: 1,
            transition: 'visibility 200ms linear, opacity 200ms linear',
            visibility: 'visible',
        },
        progress: {
            opacity: 1,
            transition: 'visibility 200ms linear, opacity 200ms linear',
            visibility: 'visible',
            position: 'absolute',
            right: theme.spacing(2.5),
        },
        hidden: {
            opacity: 0,
            visibility: 'hidden',
        },
        divider: {
            height: 24,
            margin: theme.spacing(0, 0.5),
        },
    });

interface PoolAutocompleteListProps<T extends BaseAutocompleteSuggestionType> extends WithStyles<typeof styles> {
    id?: string;
    name?: string;
    data: T[];
    order?: string[];
    exclude?: string[];
    /**
     * Columns cut to one line with "…", the full text on hover. A long address
     * otherwise widened the list past the window and pushed the columns after
     * it out of sight (R-225).
     */
    truncate?: string[];
    getSuggestionValue: (suggestion: T) => string;
    getDisplayValue?: (suggestion: T) => string;
    handleSuggestionSelected?: (data: T | null) => void;
    onValueChanged?: (value: string) => void;
    placeholder?: string;
    label?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: JSX.Element | null;
    clearInputOnSelect?: boolean;
    footer?: ReactNode;
    initialValue?: string;
    initialSuggestionValue?: T;
    readOnly?: boolean;
    inputRef?: (ref: HTMLInputElement) => void;
    disablePreventOverflow?: boolean;
    adornment?: ReactNode;
    alwaysShowSuggestions?: boolean;
    /**
     * Shown in place of the empty list when a search found nothing. An empty
     * dropdown leaves the reader guessing whether the search ran at all
     * (Prüfbericht Roland Niedermayr, 28.09.2026, Punkt 6).
     */
    emptyMessage?: ReactNode;
    showProgress?: boolean;
    autoFocus?: boolean;
}

interface PoolAutocompleteListState<T> {
    value: string;
    suggestions: T[];
}

export interface BaseAutocompleteSuggestionType {
    id: string | number;
    // Generic index signature
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [property: string]: any;
    actions?: ReactElement<ReactNode>;
}

// FIXME shouldn't lose generic type when wrapping component in withStyles
export const PoolAutocompleteList = withStyles(styles)(
    class PoolAutocompleteListBase<T extends BaseAutocompleteSuggestionType> extends Component<
        PoolAutocompleteListProps<T>,
        PoolAutocompleteListState<T>
    > {
        private rootNode = createRef<Autosuggest<T, T>>();

        public getDisplayValue = (props: PoolAutocompleteListProps<T>) => {
            if (props.initialSuggestionValue) {
                if (this.props.getDisplayValue) {
                    return this.props.getDisplayValue(props.initialSuggestionValue);
                }
                const suggestion = this.props.getSuggestionValue(props.initialSuggestionValue);
                if (suggestion) {
                    return suggestion;
                }
            }
            return props.initialValue || '';
        };

        public state: PoolAutocompleteListState<T> = {
            value: this.getDisplayValue(this.props),
            suggestions: [],
        };

        private hasActions = () => {
            let found = false;

            this.props.data.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key === 'action' || key === 'actions') {
                        found = true;
                    }
                });
            });

            return found;
        };

        // Tell Autosuggest how to search for suggestions
        public getSuggestions = (value: string, props: PoolAutocompleteListProps<T> = this.props) => {
            const inputValue = value ? value.trim().toLowerCase() : '';

            if (props.onValueChanged) {
                props.onValueChanged(inputValue);
                return props.data;
            }

            if (inputValue.length === 0) {
                return props.data;
            }

            return props.data.filter(data => {
                let found = false;
                const keys: (keyof T)[] = Object.keys(data);
                keys.forEach(key => {
                    // Convert all numbers to strings to make them searchable
                    if (typeof data[key] === 'number') {
                        data[key] = data[key].toString();
                    }

                    if (typeof data[key] === 'string' && data[key].toLowerCase().indexOf(inputValue) >= 0) {
                        found = true;
                    }
                });
                return found;
            });
        };

        public renderSuggestionContainer = (options: RenderSuggestionsContainerParams) => {
            const { containerProps, children } = options;

            if (!this.rootNode.current?.input) {
                return null;
            }

            const { key, ...restContainerProps } = containerProps;

            return (
                <Popper
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: 'preventOverflow',
                            enabled: !this.props.disablePreventOverflow,
                            options: {
                                boundariesElement: 'scrollParent',
                            },
                        },
                        {
                            name: 'flip',
                            enabled: true,
                        },
                        {
                            name: 'hide',
                            enabled: false,
                        },
                    ]}
                    style={
                        this.rootNode.current?.input && {
                            minWidth: this.rootNode.current.input.clientWidth,
                            // Never past the window's edge: a long row pushed the
                            // page sideways and over the navigation (R-225).
                            maxWidth: Math.max(
                                this.rootNode.current.input.clientWidth,
                                window.innerWidth - this.rootNode.current.input.getBoundingClientRect().left - 16
                            ),
                            zIndex: 99999,
                        }
                    }
                    anchorEl={this.rootNode.current.input}
                    open={true}
                    onClick={this.noop}
                >
                    {() => {
                        const empty = children ? null : this.renderEmptyMessage();
                        const { className, ...rest } = restContainerProps as { className?: string };

                        return (
                            <div
                                {...rest}
                                key={key}
                                // A list with nothing in it is closed as far as
                                // Autosuggest is concerned, so it withholds the
                                // class that makes the panel a panel — and the
                                // message hung over the page without one.
                                className={[className, empty ? this.props.classes.suggestionsContainerOpen : null]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                <div
                                    key={1}
                                    className={this.props.footer ? this.props.classes.suggestionFooterListWrapper : ''}
                                >
                                    {children ?? empty}
                                </div>
                                {!!children && this.props.footer && (
                                    <div className={this.props.classes.suggestionFooter}>{this.props.footer}</div>
                                )}
                            </div>
                        );
                    }}
                </Popper>
            );
        };

        /** What stands in the list when the search came back with nothing. */
        public renderEmptyMessage = () => {
            // What the search brought back, not what the open list currently
            // holds: closing the list empties that state, and the message then
            // claimed "nothing found" over twenty results (#2210, R-13).
            if (
                !this.props.emptyMessage ||
                this.props.showProgress ||
                !this.state.value ||
                this.props.data.length > 0
            ) {
                return null;
            }

            return (
                <MenuItem component="div" disabled={true}>
                    {this.props.emptyMessage}
                </MenuItem>
            );
        };

        public renderSuggestion = (suggestion: T, params: RenderSuggestionParams) => {
            const menuChildren: ReactNode[] = [];

            const exclude = this.props.exclude || [];
            const order = this.props.order || [];
            const foundKeys = Object.keys(suggestion);
            const sortedKeys: string[] = [];

            order.forEach(key => {
                if (Object.prototype.hasOwnProperty.call(suggestion, key)) {
                    sortedKeys.push(foundKeys.splice(foundKeys.indexOf(key), 1)[0]);
                }
            });

            [...sortedKeys, ...foundKeys]
                .filter(key => !exclude.includes(key))
                .forEach((key, index) => {
                    if (typeof suggestion[key] === 'string') {
                        const matches = match(suggestion[key], params.query);
                        const parts = parse(suggestion[key], matches);
                        const text = parts.map((part, partIndex) => {
                            return part.highlight ? (
                                <span key={partIndex} className={this.props.classes.highlighted}>
                                    {part.text}
                                </span>
                            ) : (
                                <span key={partIndex}>{part.text}</span>
                            );
                        });
                        menuChildren.push(
                            <div key={key}>
                                {this.props.truncate?.includes(key) ? (
                                    <span className={this.props.classes.truncated} title={suggestion[key]}>
                                        {text}
                                    </span>
                                ) : (
                                    text
                                )}
                            </div>
                        );
                    } else {
                        menuChildren.push(
                            <div
                                key={'action' + index}
                                className={key === 'actions' ? this.props.classes.suggestionActions : ''}
                            >
                                {suggestion[key]}
                            </div>
                        );
                    }
                });

            return (
                <MenuItem
                    key={suggestion.id}
                    selected={params.isHighlighted}
                    component="div"
                    className={
                        !this.hasActions
                            ? this.props.classes.suggestionItem
                            : `${this.props.classes.suggestionItem} ${this.props.classes.suggestionActionContainer}`
                    }
                >
                    {menuChildren}
                </MenuItem>
            );
        };

        public shouldRenderSuggestions = (value: string) => {
            return (value && value.length > 0) || !!this.props.alwaysShowSuggestions;
        };

        private processInputRef = (inputPropsRef: (ref: HTMLInputElement) => void) => (inputRef: HTMLInputElement) => {
            if (this.props.inputRef) {
                this.props.inputRef(inputRef);
            }
            if (inputPropsRef) {
                inputPropsRef(inputRef);
            }
        };

        private handleClear = () => {
            this.setState({
                value: '',
            });

            if (this.props.handleSuggestionSelected) {
                this.props.handleSuggestionSelected(null);
            }
        };

        private handleBlur =
            (callback: (event: FocusEvent<HTMLInputElement>) => void) => (event: FocusEvent<HTMLInputElement>) => {
                if (!event.currentTarget.value) {
                    this.setState({ value: '' });
                    if (this.props.handleSuggestionSelected) {
                        this.props.handleSuggestionSelected(null);
                    }
                }
                if (callback) {
                    callback(event);
                }
            };

        private noop = (e: MouseEvent) => e.stopPropagation();

        // Autosuggest typings are broken, they use own definitions of input props
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public renderInputComponent = (inputProps: any) => {
            const { placeholder: _, label, required, disabled, error, classes, ref, ...other } = inputProps;

            return (
                <TextField
                    label={label}
                    required={required}
                    disabled={disabled}
                    error={error}
                    fullWidth={true}
                    className={classes.field}
                    InputProps={{
                        classes: { disabled: classes.inputDisabled },
                        inputRef: this.processInputRef(ref),
                        endAdornment: !disabled && inputProps.value && (
                            <InputAdornment position="end">
                                {this.props.adornment}
                                <CircularProgress
                                    className={`${classes.progress} ${!this.props.showProgress && classes.hidden}`}
                                    size={24}
                                />
                                <span className={classes.clearGroup}>
                                    {this.props.adornment && (
                                        <Divider orientation="vertical" className={classes.divider} />
                                    )}
                                    <IconButton
                                        component="div"
                                        onClick={this.handleClear}
                                        className={`${classes.clearButton} ${
                                            this.props.showProgress && classes.hidden
                                        }`}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </span>
                            </InputAdornment>
                        ),
                        ...other,
                        autoComplete: 'no',
                        onBlur: this.handleBlur(other.onBlur),
                    }}
                    onClick={this.noop}
                    InputLabelProps={{ disabled }}
                    placeholder={this.props.placeholder}
                    helperText={this.props.helperText}
                />
            );
        };

        public onSuggestionSelected = (_event: FormEvent, data: SuggestionSelectedEventData<T>) => {
            if (this.props.clearInputOnSelect) {
                this.setState({
                    value: '',
                });
            }

            if (this.props.handleSuggestionSelected) {
                this.props.handleSuggestionSelected(data.suggestion);
            }
        };

        public onChange = (event: FormEvent<HTMLElement>, params: ChangeEvent) => {
            this.setState({ value: params.newValue });
        };

        public onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
            this.setState({ suggestions: this.getSuggestions(params.value) });
        };

        public onSuggestionsClearRequested = () => {
            this.setState({ suggestions: [] });
        };

        public UNSAFE_componentWillReceiveProps(props: PoolAutocompleteListProps<T>) {
            const partialState: Pick<PoolAutocompleteListState<T>, 'suggestions' | 'value'> =
                // Enforce correct typings
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                { suggestions: props.data } as any;

            if (
                props.initialSuggestionValue !== this.props.initialSuggestionValue ||
                props.initialValue !== this.props.initialValue
            ) {
                partialState.value = this.getDisplayValue(props);
            }

            this.setState(partialState);
        }

        public render() {
            const { suggestions, value } = this.state;
            const { id, classes, readOnly, label } = this.props;

            const inputProps = {
                value,
                placeholder: this.props.placeholder,
                label,
                required: this.props.required,
                disabled: this.props.disabled,
                onChange: this.onChange,
                error: this.props.error,
                id: id || '',
                readOnly,
                classes,
                autoFocus: this.props.autoFocus,
                name: this.props.name,
            };

            return readOnly ? (
                <PoolTextField
                    readOnly={readOnly}
                    fullWidth={true}
                    label={label}
                    value={value}
                    endAdornment={
                        this.props.adornment && <InputAdornment position="end">{this.props.adornment}</InputAdornment>
                    }
                />
            ) : (
                <Autosuggest
                    ref={this.rootNode}
                    theme={{
                        container: classes.container,
                        suggestionsContainerOpen: classes.suggestionsContainerOpen,
                        suggestionsList: classes.suggestionsList,
                        suggestion: classes.suggestion,
                    }}
                    suggestions={suggestions}
                    onSuggestionSelected={this.onSuggestionSelected}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    getSuggestionValue={this.props.getSuggestionValue}
                    renderSuggestionsContainer={this.renderSuggestionContainer}
                    renderSuggestion={this.renderSuggestion}
                    renderInputComponent={this.renderInputComponent}
                    inputProps={inputProps}
                    shouldRenderSuggestions={this.shouldRenderSuggestions}
                />
            );
        }
    }
);
